<template>
    <div>
        <card-header title="Edit Attachment" icon="fa-pencil"/>

        <card-body>
            <card-list>
                <subheader-list-item icon="fa-address-card" :title="primary"/>
                <container-list-item padded>
                    <div class="data">
                        <data-row mini title="Type">{{typeLabel}}</data-row>
                        <data-row mini title="Name" v-if="link.company && link.name">{{link.name}}</data-row>
                        <data-row mini title="Email" v-if="link.email">{{link.email}}</data-row>
                        <data-row mini title="Phone" v-if="link.phone">{{link.phone}}</data-row>
                    </div>
                    <!-- The directory record is shared across every trip that
                         uses it, so editing it is a deliberate second step
                         rather than something you fall into while annotating
                         one booking. -->
                    <b-button type="is-primary is-outlined" size="is-small" class="mt-3" @click="openDirectory">
                        <i class="fas fa-address-book mr-2"></i>Edit Directory Record
                    </b-button>
                </container-list-item>

                <subheader-list-item icon="fa-link" title="This attachment"/>
                <container-list-item padded>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Role Here" label-position="inside">
                                <b-select v-model="link.role" expanded>
                                    <option :value="null">Same as type ({{typeLabel}})</option>
                                    <option v-for="role in roles" :value="role.value" :key="role.value">{{role.label}}</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Sort" label-position="inside">
                                <b-input type="number" v-model.number="link.sort"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Label" label-position="inside">
                                <b-input v-model="link.label" placeholder="e.g. After-hours line"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <!-- Scoped to this attachment only -- facts true on
                                 every trip belong on the directory record. -->
                            <b-field label="Notes for this use" label-position="inside">
                                <b-input type="textarea" v-model="link.link_notes"></b-input>
                            </b-field>
                        </div>
                    </div>
                </container-list-item>
            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column">
                    <b-button type="is-warning is-outlined" size="is-small" @click="detach">
                        <i class="far fa-unlink"></i>
                        <span class="ml-2" v-if="confirmDetach">Remove from here?</span>
                    </b-button>
                </div>
                <div class="column has-text-right">
                    <b-button type="is-primary" size="is-small" @click="submit">
                        <i class="fas fa-check mr-2"></i>Save
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "../../TIER/components/CardHeader";
    import CardBody from "../../TIER/components/CardBody";
    import CardFooter from "../../TIER/components/CardFooter";
    import CardList from "../../TIER/components/CardList";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem";
    import ContainerListItem from "../../TIER/components/ContainerListItem";
    import DataRow from "../../TIER/components/DataRow";
    import {client as http} from "../../http_client";
    import {TYPES, typeLabel} from "@/components/contact_meta";

    /**
     * Edits the LINK, not the directory record: what this contact does here,
     * and any note true only of this booking. Detaching removes the link and
     * leaves the contact in the directory.
     */
    export default {
        components: {DataRow, ContainerListItem, SubheaderListItem, CardList, CardFooter, CardBody, CardHeader},
        props: ['card', 'props'],
        data: function () {
            return {
                link: {},
                roles: TYPES,
                confirmDetach: false,
                dirty: null
            };
        },
        computed: {
            primary: function () {
                return this.link.company || this.link.name || 'Contact';
            },
            typeLabel: function () {
                return typeLabel(this.link.type);
            }
        },
        methods: {
            loadLink: function () {
                this.$emit('loading', true);
                http.get('/api/contacts/links/' + this.props.linkId, {force: true}).then(response => {
                    this.link = response.data;
                    this.$emit('loading', false);
                });
            },
            openDirectory: function () {
                this.$openCard('edit-contact', {contactId: this.link.id}, this.card);
            },
            submit() {
                this.$emit('loading', true);
                http.put('/api/contacts/links/' + this.props.linkId, {
                    role: this.link.role,
                    label: this.link.label,
                    // The join aliases entity_contacts.notes to link_notes so it
                    // doesn't collide with the directory record's own notes.
                    notes: this.link.link_notes,
                    sort: this.link.sort
                }, {force: true}).then(() => {
                    this.$saveAlert(false);
                    this.$closeCard(this.card);
                }).catch(() => {
                    this.$emit('loading', false);
                });
            },
            detach() {
                if (this.confirmDetach) {
                    this.$emit('loading', true);
                    http.delete('/api/contacts/links/' + this.props.linkId).then(() => {
                        this.$saveAlert(false);
                        this.$closeCard(this.card);
                    }).catch(() => {
                        this.$emit('loading', false);
                    });
                } else {
                    this.confirmDetach = true;
                    setTimeout(() => {
                        this.confirmDetach = false;
                    }, 3000);
                }
            }
        },
        watch: {
            'props.linkId': function () {
                this.dirty = null;
                this.loadLink();
            },
            'link': {
                deep: true,
                handler: function () {
                    this.dirty = (this.dirty !== null);
                    if (!this.$TIER.saveAlert.active && this.dirty)
                        this.$saveAlert(true, 'Are you done editing this attachment? Unsaved changes will be lost.');
                }
            }
        },
        mounted() {
            this.loadLink();
        }
    };
</script>
